/**
 * The plan's DAG laid out for React Flow (docs/plans/change-planner.md D8): dagre, top to bottom (the
 * graph's large view has the height; a chain left to right only fit far zoomed out), one node per step
 * and one edge per dependency, fixed node size so no measuring pass is needed.
 * Pure, so it is unit-tested.
 */
import dagre from '@dagrejs/dagre';
import type { Edge, Node } from '@xyflow/react';
import type { plans } from './client';

export const NODE_WIDTH = 240;
export const NODE_HEIGHT = 84;

export interface StepNodeData extends Record<string, unknown> {
  step: plans.PlanStepView;
  selected: boolean;
}

export type StepNode = Node<StepNodeData, 'step'>;

/**
 * Each step's waits less those another of its waits already implies (a step after the queue's create and after a step
 * that is itself after the create draws one edge, not two): the graph shows the order, not every dependency. Pure.
 */
export function directWaits(steps: readonly Pick<plans.PlanStepView, 'key' | 'depends_on'>[]): Map<string, string[]> {
  const deps = new Map(steps.map((step) => [step.key, step.depends_on]));
  const reach = new Map<string, Set<string>>();
  const reachOf = (key: string, seen = new Set<string>()): Set<string> => {
    const known = reach.get(key);
    if (known !== undefined) return known;
    const out = new Set<string>();
    for (const dep of deps.get(key) ?? []) {
      if (seen.has(dep)) continue;
      out.add(dep);
      for (const further of reachOf(dep, new Set([...seen, key]))) out.add(further);
    }
    reach.set(key, out);
    return out;
  };
  return new Map(steps.map((step) => [step.key, step.depends_on.filter((dep) => !step.depends_on.some((other) => other !== dep && reachOf(other).has(dep)))]));
}

export function layoutPlan(steps: readonly plans.PlanStepView[], selectedKey: string | null): { nodes: StepNode[]; edges: Edge[] } {
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: 'TB', nodesep: 32, ranksep: 48, marginx: 16, marginy: 16 });
  g.setDefaultEdgeLabel(() => ({}));
  for (const step of steps) g.setNode(step.key, { width: NODE_WIDTH, height: NODE_HEIGHT });
  const keys = new Set(steps.map((step) => step.key));
  const edges: Edge[] = [];
  const direct = directWaits(steps);
  for (const step of steps) {
    for (const dep of direct.get(step.key) ?? []) {
      if (!keys.has(dep)) continue;
      g.setEdge(dep, step.key);
      edges.push({
        id: `${dep}->${step.key}`, source: dep, target: step.key, type: 'smoothstep',
        animated: step.run?.status === 'running',
      });
    }
  }
  dagre.layout(g);
  const nodes: StepNode[] = steps.map((step) => {
    const at = g.node(step.key);
    return {
      id: step.key, type: 'step', data: { step, selected: step.key === selectedKey },
      position: { x: at.x - NODE_WIDTH / 2, y: at.y - NODE_HEIGHT / 2 },
      width: NODE_WIDTH, height: NODE_HEIGHT,
    };
  });
  return { nodes, edges };
}
